

import { useState } from 'react';
import { AuditLogList } from '@repo/ui/features/audit-logs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@repo/ui/components/ui/card';
import { FileText, AlertTriangle, ShieldAlert, Info } from 'lucide-react';
import { useAuditStats } from '@repo/ui/hooks/api/audit-logs';
import { subDays } from 'date-fns';
import { AuditLog } from '@repo/ui/lib/api/types/audit-log.types';
import { PageHeader } from '@repo/ui/ui/layout/page-header';

export default function AuditLogsPage() {
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  // Fetch stats for the last 7 days
  const startDate = subDays(new Date(), 7).toISOString();
  const endDate = new Date().toISOString();
  const { data: stats } = useAuditStats(startDate, endDate);

  const handleViewDetails = (log: AuditLog) => {
    setSelectedLog(log);
    // TODO: Open modal or navigate to detail page
    console.log('View details:', log);
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <PageHeader>
        <PageHeader.Title>Journaux d'Audit</PageHeader.Title>
        <PageHeader.Description>
          Consultez l'historique des actions utilisateurs et système
        </PageHeader.Description>
      </PageHeader>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Logs</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total.toLocaleString() || 0}</div>
            <p className="text-xs text-muted-foreground">
              derniers 7 jours
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Erreurs</CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {stats?.bySeverity?.ERROR || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              taux: {stats?.errorRate ? (stats.errorRate * 100).toFixed(2) : 0}%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Critiques</CardTitle>
            <ShieldAlert className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {stats?.bySeverity?.CRITICAL || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              nécessitent attention
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Durée Moyenne</CardTitle>
            <Info className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.avgDuration ? Math.round(stats.avgDuration) : 0}ms
            </div>
            <p className="text-xs text-muted-foreground">
              temps de réponse
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main List */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des événements</CardTitle>
          <CardDescription>
            Historique complet des actions et événements système
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AuditLogList
            defaultFilters={{
              limit: 50,
            }}
            enableAutoRefresh
            refreshInterval={30000}
            onViewDetails={handleViewDetails}
          />
        </CardContent>
      </Card>
    </div>
  );
}
